import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronRight,
  FileText, Sparkles, Loader2,
} from "lucide-react";
import { PageShell } from "@/components/SidebarLayout";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { formatCr } from "@/lib/format";

const HORIZON = 17;

const EMPTY = {
  name: "", lender: "", kind: "loan", project_names: [],
  sanctioned: 0, interest_rate: 0.11, opening_balance: 0,
  start_date: "2026-08-01",
  sweep_basis: "collections",
  slabs: [],
  drawdowns: {},
  project_shares: [],
};

function nextMonths(startIso, n = 18) {
  const [y, m] = startIso.split("-").map(Number);
  const out = [];
  for (let i = 0; i < n; i++) {
    const idx = (y * 12 + (m - 1)) + i;
    const ny = Math.floor(idx / 12);
    const nm = (idx % 12) + 1;
    out.push(`${ny.toString().padStart(4,"0")}-${nm.toString().padStart(2,"0")}-01`);
  }
  return out;
}
function monthLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
}
function formatRate(rate) {
  if (!rate) return "0%";
  const s = (rate * 100).toFixed(3).replace(/\.?0+$/, "");
  return `${s}%`;
}

export default function LoanFacilities() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "finance";
  const [facilities, setFacilities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sweepRules, setSweepRules] = useState([]);
  const [impact, setImpact] = useState({});
  const [schedule, setSchedule] = useState({});   // id → {rows, cap_bound_months}
  const [engineSchedule, setEngineSchedule] = useState({}); // id → engine-preview rows
  const [months, setMonths] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("facilities");
  const [rulePreview, setRulePreview] = useState(null); // { rule, data }
  const [applyingRule, setApplyingRule] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, p, s, imp, sch, eng] = await Promise.all([
        api.get("/loans/facilities", { params: { horizon: HORIZON } }),
        api.get("/projects"),
        api.get("/sweep-rules"),
        api.get("/loans/facilities/impact", { params: { horizon: HORIZON } }),
        api.get("/loans/schedule", { params: { horizon: HORIZON } }),
        api.get("/loans/schedule", { params: { horizon: HORIZON, engine: 1 } }),
      ]);
      setFacilities(f.data);
      setProjects(p.data.filter((x) => x.type !== "land"));
      setSweepRules(s.data);
      setImpact(Object.fromEntries((imp.data.facilities || []).map((i) => [i.id, i])));
      setSchedule(Object.fromEntries((sch.data.facilities || []).map((x) => [x.id, x])));
      setEngineSchedule(Object.fromEntries((eng.data.facilities || []).map((x) => [x.id, x])));
      setMonths(sch.data.months || []);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    try {
      if (f.id) await api.put(`/loans/facilities/${f.id}`, f);
      else await api.post("/loans/facilities", f);
      toast.success("Saved — Cashflow + Debt Schedule updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this facility?")) return;
    try { await api.delete(`/loans/facilities/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) }); }
  };

  const applyRule = async (rule) => {
    setApplyingRule(true);
    try {
      await api.put(`/sweep-rules/${encodeURIComponent(rule.project)}`, rule);
      toast.success(`Sweep rule for ${rule.project} saved`, { description: "Sweep-engine preview updated — the Cashflow ledger stays on the Excel schedule." });
      setRulePreview(null);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setApplyingRule(false);
    }
  };

  const saveRule = async (rule) => {
    try {
      const res = await api.post("/sweep-rules/preview", rule, { params: { horizon: HORIZON } });
      if (res.data.changed) setRulePreview({ rule, data: res.data });
      else await applyRule(rule);
    } catch (e) {
      toast.error("Preview failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const saveSchedule = async (fid, monthly_principal, monthly_interest) => {
    try {
      await api.put(`/loans/facilities/${fid}`, { monthly_principal, monthly_interest });
      toast.success("Schedule saved — Cashflow + Debt Schedule updated");
      await load();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const loans = facilities.filter((f) => f.kind !== "fixed_obligation");
  const fixedObs = facilities.filter((f) => f.kind === "fixed_obligation");

  return (
    <PageShell
      testId="loans-facilities-page"
      kicker="Loans"
      title="Loan Facilities"
      hideProjectFilter
      actions={canEdit && !editing && (
        <button data-testid="facility-add-btn" onClick={() => setEditing({ ...EMPTY })}
          className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium">
          <Plus size={13} /> Add facility
        </button>
      )}
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-3xl">
        Every facility pre-loads its <b>Excel schedule</b>. Expand a row to see the month-by-month
        Principal &amp; Interest grid — edit any cell, hit Save, and the Cashflow ledger + Debt
        Schedule update instantly. Facilities linked to a sweep rule also show the engine-computed
        principal as a read-only preview row — the saved Excel schedule stays the source of truth.
        {!canEdit && <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold" style={{ background: "var(--cf-accent-soft)", color: "var(--cf-accent-deep)" }}>View-only · Editable by Finance</span>}
      </p>

      {/* Tabs */}
      <div className="inline-flex items-center gap-1 p-1 bg-surface rounded-full" data-testid="lf-tabs"
        style={{ boxShadow: "0 1px 2px rgba(18,49,89,0.05), 0 6px 16px rgba(18,49,89,0.06)" }}>
        {[["facilities", "Facilities"], ["rules", `Sweep Rules · ${sweepRules.length}`]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`lf-tab-${k}`}
            className={`cf-touch px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === k ? "bg-ink text-paper" : "text-muted-dark hover:text-ink"}`}>
            {l}
          </button>
        ))}
      </div>

      {editing && (
        <FacilityEditor
          value={editing}
          projects={projects}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {rulePreview && (
        <SweepPreviewModal
          preview={rulePreview}
          applying={applyingRule}
          onCancel={() => setRulePreview(null)}
          onApply={() => applyRule(rulePreview.rule)}
        />
      )}

      {tab === "facilities" && !editing && (
        <div className="space-y-6">
          <FacilityTable
            title="Loan Facilities"
            testId="lf-table"
            rows={loans}
            months={months}
            schedule={schedule}
            engineSchedule={engineSchedule}
            impact={impact}
            sweepRules={sweepRules}
            canEdit={canEdit}
            onEdit={(f) => setEditing(f)}
            onDelete={del}
            onSaveSchedule={saveSchedule}
          />
          {fixedObs.length > 0 && (
            <FacilityTable
              title="Fixed Obligations"
              subtitle="EMI / fee rows — no drawdown lifecycle"
              testId="lf-fixed-table"
              rows={fixedObs}
              months={months}
              schedule={schedule}
              engineSchedule={engineSchedule}
              impact={impact}
              sweepRules={sweepRules}
              canEdit={canEdit}
              onEdit={(f) => setEditing(f)}
              onDelete={del}
              onSaveSchedule={saveSchedule}
              compact
            />
          )}
          {facilities.length === 0 && (
            <div className="cf-card p-8 text-center text-sm text-muted-dark">
              No facilities yet. {canEdit && <>Click <b>Add facility</b> to configure the first one.</>}
            </div>
          )}
        </div>
      )}

      {tab === "rules" && !editing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="lf-rules-panel">
          {sweepRules.map((rule) => {
            const linked = facilities.filter((f) =>
              (f.project_shares?.length ? f.project_shares.map((s) => s.project) : f.project_names || []).includes(rule.project)
            );
            const capMonths = Math.max(0, ...linked.map((f) => impact[f.id]?.cap_bound_count || 0));
            return (
              <SweepRuleCard
                key={rule.project}
                rule={rule}
                canEdit={canEdit}
                onSave={saveRule}
                scope=""
                capMonths={capMonths}
                capHorizon={HORIZON}
              />
            );
          })}
          {sweepRules.length === 0 && (
            <div className="cf-card p-8 text-center text-sm text-muted-dark">No sweep rules configured.</div>
          )}
        </div>
      )}
    </PageShell>
  );
}

/* ─────────────────────────── Facility Table ─────────────────────────── */

function FacilityTable({ title, subtitle, testId, rows, months, schedule, engineSchedule, impact, sweepRules, canEdit, onEdit, onDelete, onSaveSchedule, compact }) {
  const [openId, setOpenId] = useState(null);
  const totals = rows.reduce((a, f) => ({
    sanctioned: a.sanctioned + (f.sanctioned || 0),
    repaid: a.repaid + (f.repaid_total || 0),
    drawn: a.drawn + (f.drawn_total || 0),
    notDrawn: a.notDrawn + (f.not_drawn || 0),
  }), { sanctioned: 0, repaid: 0, drawn: 0, notDrawn: 0 });

  return (
    <section className="cf-card p-0 overflow-hidden" data-testid={testId}>
      <header className="px-4 md:px-5 py-3 border-b border-hairline flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-display text-base text-ink font-bold">{title}</h3>
          {subtitle && <p className="text-[11px] text-muted-dark">{subtitle}</p>}
        </div>
        <span className="text-[11px] text-muted-light">{rows.length} rows · all ₹Cr</span>
      </header>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/70" style={{ background: "var(--cf-surface-2)" }}>
              <th className="text-left font-semibold px-4 py-2.5 min-w-[200px]">Facility</th>
              {!compact && <th className="text-left font-semibold px-3 py-2.5">Lender</th>}
              <th className="text-right font-semibold px-3 py-2.5">Rate</th>
              <th className="text-right font-semibold px-3 py-2.5">Sanctioned</th>
              <th className="text-right font-semibold px-3 py-2.5">Repaid</th>
              <th className="text-right font-semibold px-3 py-2.5">Drawn</th>
              <th className="text-right font-semibold px-3 py-2.5">Not Drawn</th>
              <th className="text-left font-semibold px-3 py-2.5">Source</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <FacilityRow
                key={f.id}
                f={f}
                months={months}
                sched={schedule[f.id]}
                engineSched={engineSchedule[f.id]}
                impact={impact[f.id]}
                sweepRules={sweepRules}
                canEdit={canEdit}
                compact={compact}
                open={openId === f.id}
                onToggle={() => setOpenId(openId === f.id ? null : f.id)}
                onEdit={() => onEdit(f)}
                onDelete={() => onDelete(f.id)}
                onSaveSchedule={onSaveSchedule}
              />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-hairline text-xs font-bold" style={{ background: "var(--cf-surface-2)" }}>
                <td className="px-4 py-2.5 text-ink">Total</td>
                {!compact && <td />}
                <td />
                <td className="px-3 py-2.5 text-right num text-ink">{formatCr(totals.sanctioned, { showZero: true })}</td>
                <td className="px-3 py-2.5 text-right num text-ink">{formatCr(totals.repaid, { showZero: true })}</td>
                <td className="px-3 py-2.5 text-right num text-ink">{formatCr(totals.drawn, { showZero: true })}</td>
                <td className="px-3 py-2.5 text-right num text-ink">{formatCr(totals.notDrawn, { showZero: true })}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function FacilityRow({ f, months, sched, engineSched, impact, sweepRules, canEdit, compact, open, onToggle, onEdit, onDelete, onSaveSchedule }) {
  const linkedProjects = (f.project_shares?.length ? f.project_shares.map((s) => s.project) : f.project_names) || [];
  const hasRule = linkedProjects.some((p) => sweepRules.find((r) => r.project === p));
  const hasOverride = Object.keys(f.monthly_principal || {}).length > 0 || Object.keys(f.monthly_interest || {}).length > 0;
  const source = hasOverride
    ? { label: "Excel", bg: "#FEF3C7", fg: "#92400E" }
    : { label: "Computed", bg: "var(--cf-surface-2)", fg: "var(--cf-muted-dark, #64748B)" };

  const colSpan = compact ? 8 : 9;
  return (
    <>
      <tr
        className="border-b border-hairline/40 hover:bg-surface-2/40 cursor-pointer transition-colors"
        data-testid={`lf-row-${f.name}`}
        onClick={onToggle}
      >
        <td className="px-4 py-2.5 min-w-[200px]">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={13} className="text-muted-light shrink-0" /> : <ChevronRight size={13} className="text-muted-light shrink-0" />}
            <div className="min-w-0">
              <div className="text-ink font-semibold truncate">{f.name}</div>
              {linkedProjects.length > 0 && (
                <div className="text-[10px] text-muted-light truncate">{linkedProjects.join(" · ")}</div>
              )}
            </div>
          </div>
        </td>
        {!compact && <td className="px-3 py-2.5 text-muted-dark whitespace-nowrap">{f.lender || "—"}</td>}
        <td className="px-3 py-2.5 text-right num text-ink whitespace-nowrap">{formatRate(f.interest_rate)}</td>
        <td className="px-3 py-2.5 text-right num font-semibold text-ink whitespace-nowrap" data-testid={`lf-sanctioned-${f.name}`}>{formatCr(f.sanctioned, { showZero: true })}</td>
        <td className="px-3 py-2.5 text-right num text-ink whitespace-nowrap" data-testid={`lf-repaid-${f.name}`}>{formatCr(f.repaid_total, { showZero: true })}</td>
        <td className="px-3 py-2.5 text-right num text-ink whitespace-nowrap" data-testid={`lf-drawn-${f.name}`}>{formatCr(f.drawn_total, { showZero: true })}</td>
        <td className="px-3 py-2.5 text-right num font-semibold whitespace-nowrap" style={{ color: (f.not_drawn || 0) > 0.005 ? "#059669" : "var(--cf-muted-dark, #64748B)" }} data-testid={`lf-notdrawn-${f.name}`}>
          {formatCr(f.not_drawn, { showZero: true })}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">
          <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold"
            style={{ background: source.bg, color: source.fg }}
            data-testid={`lf-source-${f.name}`}>
            {source.label}
          </span>
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap text-right">
          {canEdit && (
            <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button data-testid={`facility-edit-${f.name}`} onClick={onEdit} className="text-muted-dark hover:text-ink" title="Edit facility"><Edit2 size={13} /></button>
              <button data-testid={`facility-delete-${f.name}`} onClick={onDelete} className="text-muted-dark hover:text-red-500" title="Delete facility"><Trash2 size={13} /></button>
            </span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-hairline/40" data-testid={`lf-grid-wrap-${f.name}`}>
          <td colSpan={colSpan} className="p-0">
            <ScheduleGrid
              f={f}
              months={months}
              sched={sched}
              engineSched={engineSched}
              impact={impact}
              hasRule={hasRule}
              canEdit={canEdit}
              onSave={onSaveSchedule}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/* ─────────────────── Expanded monthly schedule grid ─────────────────── */

function ScheduleGrid({ f, months, sched, engineSched, impact, hasRule, canEdit, onSave }) {
  // Exact internal ₹L values from the live schedule — never rounded. Inputs
  // display 2-dec Cr; on save only the cells the user actually edited are
  // overlaid on the exact baseline, so untouched months keep their sub-₹L
  // precision and a revert-to-displayed-value is a true no-op.
  const initFrom = useCallback(
    (src) => Object.fromEntries(months.map((m) => [m, Number(src?.[m] || 0)])),
    [months],
  );
  const [initial, setInitial] = useState(() => ({
    principal: initFrom(sched?.rows?.principal),
    interest: initFrom(sched?.rows?.interest),
  }));
  const [editStr, setEditStr] = useState({}); // {"principal|2026-08-01": "1.15"} raw strings
  const [saving, setSaving] = useState(false);

  // Refresh baseline when the schedule reloads after a save.
  useEffect(() => {
    setInitial({
      principal: initFrom(sched?.rows?.principal),
      interest: initFrom(sched?.rows?.interest),
    });
  }, [sched, initFrom]);

  const engineRow = engineSched?.rows?.principal || {};
  const drawdownRow = sched?.rows?.drawdown || {};
  const closingRow = sched?.rows?.closing || {};

  const cellVal = (which, m) => {
    const key = `${which}|${m}`;
    if (key in editStr) return editStr[key];
    const vL = initial[which][m];
    return vL ? (vL / 100).toFixed(2) : "";
  };

  const setCell = (which, m, s) =>
    setEditStr((x) => ({ ...x, [`${which}|${m}`]: s }));

  const buildMap = (which) => {
    const out = { ...initial[which] };
    for (const [key, s] of Object.entries(editStr)) {
      const [w, m] = key.split("|");
      if (w !== which) continue;
      const t = s.trim();
      const initCr = (initial[which][m] / 100).toFixed(2);
      if (t === initCr) continue;               // reverted to displayed initial → keep exact ₹L
      if (t === "") { out[m] = 0; continue; }
      const n = parseFloat(t);
      if (Number.isNaN(n)) continue;
      out[m] = Math.round(n * 10000) / 100;     // Cr → ₹L (2-dec Cr = whole ₹L)
    }
    return out;
  };

  // Save payload: ONLY the cells the user actually changed. Untouched months
  // are never sent, so their full-precision ₹L values survive in Mongo (the
  // schedule API serves 2-dec-rounded ₹L — echoing them all back would
  // permanently drift Excel parity). The backend merges per-month.
  const buildPatch = (which) => {
    const out = {};
    for (const [key, s] of Object.entries(editStr)) {
      const [w, m] = key.split("|");
      if (w !== which) continue;
      const t = s.trim();
      const initCr = (initial[which][m] / 100).toFixed(2);
      if (t === initCr) continue;               // reverted to displayed initial → no-op
      if (t === "") { out[m] = 0; continue; }
      const n = parseFloat(t);
      if (Number.isNaN(n)) continue;
      out[m] = Math.round(n * 10000) / 100;     // Cr → ₹L (2-dec Cr = whole ₹L)
    }
    return out;
  };

  const principalView = buildMap("principal");
  const interestView = buildMap("interest");

  const dirty = Object.entries(editStr).some(([key, s]) => {
    const [w, m] = key.split("|");
    const t = s.trim();
    const initCr = (initial[w][m] / 100).toFixed(2);
    if (t === initCr) return false;
    if (t === "") return initial[w][m] !== 0;
    const n = parseFloat(t);
    return !Number.isNaN(n) && Math.abs(Math.round(n * 10000) / 100 - initial[w][m]) > 1e-9;
  });

  const doSave = async () => {
    setSaving(true);
    await onSave(f.id, buildPatch("principal"), buildPatch("interest"));
    setSaving(false);
    setEditStr({});
  };

  const totP = Object.values(principalView).reduce((a, b) => a + b, 0);
  const totI = Object.values(interestView).reduce((a, b) => a + b, 0);

  // Impact preview — engine principal vs current principal
  const delta = impact ? Number(impact.delta || 0) / 100 : 0;
  const showDelta = impact && hasRule && Math.abs(delta) > 0.005;

  return (
    <div className="px-4 md:px-5 py-4" style={{ background: "var(--cf-surface-2)" }} data-testid={`lf-grid-${f.name}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">
          Monthly schedule · {months.length} months · inputs in ₹Cr
          {hasRule && <span className="ml-2 text-emerald-600 normal-case tracking-normal">Click <b>Adopt engine → Cashflow</b> to push the sweep-engine preview into the saved schedule (Cashflow ledger updates on Save).</span>}
        </div>
        <div className="flex items-center gap-2">
          {showDelta && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-bold num"
              style={{ background: delta > 0 ? "#FEE2E2" : "#DCFCE7", color: delta > 0 ? "#B91C1C" : "#059669" }}
              data-testid={`facility-delta-${f.name}`}
            >
              Engine Δ {delta > 0 ? "+" : ""}₹{delta.toFixed(2)}Cr
            </span>
          )}
          {canEdit && hasRule && Object.keys(engineRow).length > 0 && (
            <button
              data-testid={`lf-adopt-engine-${f.name}`}
              onClick={() => {
                const patch = {};
                for (const m of months) {
                  const engL = Number(engineRow[m] || 0);
                  const curL = initial.principal[m] || 0;
                  if (Math.abs(engL - curL) < 0.005) continue;
                  patch[`principal|${m}`] = engL ? (engL / 100).toFixed(2) : "";
                }
                if (Object.keys(patch).length === 0) {
                  toast.info("Already matches the engine — nothing to adopt.");
                  return;
                }
                setEditStr((x) => ({ ...x, ...patch }));
                toast.success(`Engine schedule loaded for ${f.name}`, { description: "Review the Principal row, then click Save to push to Cashflow." });
              }}
              className="cf-touch inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:opacity-90"
              title="Copy the sweep-engine preview values into the editable Principal row"
            >
              <Sparkles size={12} /> Adopt engine → Cashflow
            </button>
          )}
          {canEdit && (
            <button
              data-testid={`lf-grid-save-${f.name}`}
              disabled={!dirty || saving}
              onClick={doSave}
              className="cf-touch inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold disabled:opacity-50"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save schedule
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-hairline/60">
        <table className="min-w-full text-[11px] border-collapse">
          <thead>
            <tr style={{ background: "var(--cf-surface)" }}>
              <th className="text-left px-3 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold sticky left-0 z-10 min-w-[110px]" style={{ background: "var(--cf-surface)" }}>Line</th>
              {months.map((m) => (
                <th key={m} className="text-right px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold whitespace-nowrap">{monthLabel(m)}</th>
              ))}
              <th className="text-right px-3 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            <GridRow label="Drawdown" months={months} values={drawdownRow} readOnly tone="muted" />
            {hasRule && (
              <GridRow label="Principal · Sweep engine (preview)" months={months} values={engineRow} readOnly tone="muted" />
            )}
            <tr className="border-t border-hairline/40" style={{ background: "var(--cf-surface)" }}>
              <td className="px-3 py-1.5 font-semibold text-ink sticky left-0 z-10" style={{ background: "var(--cf-surface)" }}>Principal</td>
              {months.map((m) => (
                <td key={m} className="px-1 py-1 text-right">
                  {canEdit ? (
                    <input
                      type="number"
                      step="0.01"
                      value={cellVal("principal", m)}
                      placeholder="–"
                      onChange={(e) => setCell("principal", m, e.target.value)}
                      className="w-20 text-right text-[11px] px-1.5 py-0.5 rounded border border-transparent hover:border-hairline focus:border-accent bg-transparent num tabular-nums"
                      data-testid={`lf-cell-principal-${f.name}-${m}`}
                    />
                  ) : (
                    <span className="num tabular-nums text-ink px-1.5">{principalView[m] ? formatCr(principalView[m]) : "–"}</span>
                  )}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right num font-bold text-ink">{formatCr(totP, { showZero: true })}</td>
            </tr>
            <tr className="border-t border-hairline/40" style={{ background: "var(--cf-surface)" }}>
              <td className="px-3 py-1.5 font-semibold text-ink sticky left-0 z-10" style={{ background: "var(--cf-surface)" }}>Interest</td>
              {months.map((m) => (
                <td key={m} className="px-1 py-1 text-right">
                  {canEdit ? (
                    <input
                      type="number"
                      step="0.01"
                      value={cellVal("interest", m)}
                      placeholder="–"
                      onChange={(e) => setCell("interest", m, e.target.value)}
                      className="w-20 text-right text-[11px] px-1.5 py-0.5 rounded border border-transparent hover:border-hairline focus:border-accent bg-transparent num tabular-nums"
                      data-testid={`lf-cell-interest-${f.name}-${m}`}
                    />
                  ) : (
                    <span className="num tabular-nums text-ink px-1.5">{interestView[m] ? formatCr(interestView[m]) : "–"}</span>
                  )}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right num font-bold text-ink">{formatCr(totI, { showZero: true })}</td>
            </tr>
            <GridRow label="Closing" months={months} values={closingRow} readOnly tone="muted" />
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-muted-light mt-1.5">
        Saving writes these as the facility's Excel-override schedule — the Cashflow ledger, Debt Schedule and all charts refresh from it immediately.
      </p>
    </div>
  );
}

function GridRow({ label, months, values, tone }) {
  const tot = months.reduce((a, m) => a + Number(values?.[m] || 0), 0);
  return (
    <tr className="border-t border-hairline/40" style={{ background: "var(--cf-surface)" }}>
      <td className="px-3 py-1.5 text-muted-dark sticky left-0 z-10" style={{ background: "var(--cf-surface)" }}>{label}</td>
      {months.map((m) => {
        const v = Number(values?.[m] || 0);
        return (
          <td key={m} className={`px-2 py-1.5 text-right num tabular-nums whitespace-nowrap ${tone === "muted" ? "text-muted-dark" : "text-ink"}`}>
            {v ? formatCr(v) : "–"}
          </td>
        );
      })}
      <td className="px-3 py-1.5 text-right num font-semibold text-muted-dark">{tot ? formatCr(tot) : "–"}</td>
    </tr>
  );
}

/* ─────────────────────── Sweep rule preview modal ────────────────────── */

function SweepPreviewModal({ preview, applying, onCancel, onApply }) {
  const { rule, data } = preview;
  const facs = data.facilities || [];
  const port = data.portfolio || {};
  const fmtDelta = (v) => `${v >= 0 ? "+" : "−"}${formatCr(Math.abs(v), { showZero: true })}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,25,38,0.45)" }}
      data-testid="sweep-preview-modal" onClick={onCancel}>
      <div className="cf-card w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5 cf-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <div className="font-display text-lg text-ink font-bold">Sweep rule change — preview</div>
            <div className="text-xs text-muted-dark">{rule.project} · {data.horizon}-month horizon</div>
          </div>
          <button onClick={onCancel} className="text-muted-dark hover:text-ink" data-testid="sweep-preview-close"><X size={16} /></button>
        </div>
        <p className="text-[11px] text-muted-dark mb-4">
          Applying updates the <b>sweep-engine preview</b> only — the Cashflow ledger keeps running on the saved Excel schedule.
        </p>

        <div className="rounded-lg px-3 py-2.5 mb-4 flex items-center justify-between gap-3 flex-wrap"
          style={{ background: "var(--cf-surface-2)" }} data-testid="sweep-preview-portfolio">
          <span className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Portfolio debt service · engine scenario</span>
          <span className="text-sm num font-bold text-ink" data-testid="sweep-preview-portfolio-delta">
            {formatCr(port.current_debt_service, { showZero: true })} → {formatCr(port.engine_debt_service_new, { showZero: true })}
            <span className="ml-2" style={{ color: (port.delta_vs_current || 0) >= 0 ? "#B91C1C" : "#059669" }}>
              ({fmtDelta(port.delta_vs_current || 0)})
            </span>
          </span>
        </div>

        <table className="min-w-full text-xs border-collapse mb-4">
          <thead>
            <tr className="text-[9.5px] uppercase tracking-widest text-muted-light border-b border-hairline/70">
              <th className="text-left font-semibold px-2 py-2">Facility</th>
              <th className="text-right font-semibold px-2 py-2">Saved (Excel)</th>
              <th className="text-right font-semibold px-2 py-2">Engine · new</th>
              <th className="text-right font-semibold px-2 py-2">Δ vs saved</th>
              <th className="text-left font-semibold px-2 py-2">Months moved by this edit</th>
            </tr>
          </thead>
          <tbody>
            {facs.map((fa) => (
              <tr key={fa.id} className="border-b border-hairline/40" data-testid={`sweep-preview-row-${fa.name}`}>
                <td className="px-2 py-2 font-semibold text-ink">{fa.name}</td>
                <td className="px-2 py-2 text-right num">{formatCr(fa.current_principal, { showZero: true })}</td>
                <td className="px-2 py-2 text-right num font-semibold text-ink">{formatCr(fa.engine_principal_new, { showZero: true })}</td>
                <td className="px-2 py-2 text-right num font-bold" style={{ color: fa.delta_vs_current >= 0 ? "#B91C1C" : "#059669" }}
                  data-testid={`sweep-preview-delta-${fa.name}`}>
                  {fmtDelta(fa.delta_vs_current)}
                </td>
                <td className="px-2 py-2 text-muted-dark" data-testid={`sweep-preview-months-${fa.name}`}>
                  {fa.changed_months.length === 0 ? "—" : (
                    fa.changed_months.length <= 4
                      ? fa.changed_months.map(monthLabel).join(", ")
                      : `${fa.changed_months.slice(0, 4).map(monthLabel).join(", ")} +${fa.changed_months.length - 4} more`
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} data-testid="sweep-preview-cancel" className="px-3.5 py-2 text-xs text-muted-dark">Cancel</button>
          <button onClick={onApply} disabled={applying} data-testid="sweep-preview-apply"
            className="cf-touch inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-white text-xs font-semibold disabled:opacity-50">
            {applying ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Apply rule
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Sweep Rule Card ─────────────────────────── */

function SweepRuleCard({ rule, canEdit, onSave, scope = "", capMonths = 0, capHorizon = 17 }) {
  const [r, setR] = useState(rule);
  // Dirty is computed — after a save the parent refetch makes `rule` match
  // `r` again, auto-hiding the Save button; cancelling the preview modal
  // keeps the local edits (and the button) intact.
  const dirty = JSON.stringify(r) !== JSON.stringify(rule);
  useEffect(() => {
    if (!dirty) setR(rule);
  }, [rule, dirty]);
  const upd = (patch) => setR((x) => ({ ...x, ...patch }));
  const updBand = (i, k, v) =>
    setR((x) => ({ ...x, slab_bands: x.slab_bands.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addBand = () => upd({ slab_bands: [...r.slab_bands, { upto_cr: 0, rate: 0 }] });
  const delBand = (i) => upd({ slab_bands: r.slab_bands.filter((_, idx) => idx !== i) });
  const updFee = (i, k, v) =>
    setR((x) => ({ ...x, flat_fee_bands: x.flat_fee_bands.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addFee = () => upd({ flat_fee_bands: [...(r.flat_fee_bands || []), { upto_months: 12, fee_l: 0 }] });
  const delFee = (i) => upd({ flat_fee_bands: r.flat_fee_bands.filter((_, idx) => idx !== i) });

  return (
    <div className="rounded-lg p-3 border border-hairline/60" style={{ background: "var(--cf-surface-2)" }} data-testid={`sweep-rule-${scope}${rule.project}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg" style={{ background: "#F0F9FF", color: "#0369A1" }}>
            <Sparkles size={13} />
          </div>
          <div>
            <div className="font-display text-sm text-ink font-bold">{rule.project}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold flex items-center gap-1.5">
              <FileText size={10} /> Cutoff {rule.cutoff_date} · cum ₹{Number(rule.cutoff_cumulative_cr).toFixed(2)} Cr
            </div>
          </div>
        </div>
        {canEdit && dirty && (
          <button
            data-testid={`sweep-rule-save-${scope}${rule.project}`}
            onClick={() => onSave(r)}
            className="cf-touch inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold"
          >
            <Save size={12} /> Preview & save
          </button>
        )}
      </div>

      {capMonths > 0 && (
        <div
          className="mt-2 flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[10.5px]"
          style={{ background: "#FEF3C7", color: "#92400E" }}
          data-testid={`cap-note-${scope}${rule.project}`}
        >
          <span className="text-[12px]">ⓘ</span>
          <span>
            <b>Cap binding · {capMonths} of {capHorizon} months</b> — the facility's outstanding+drawdown is smaller than the computed sweep on these months, so raising this rate above the cap has <b>no effect</b>. Lowering the rate below the cap will move numbers immediately.
          </span>
        </div>
      )}

      {/* Slab bands */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Slab bands (% on fresh collection)</div>
          {canEdit && (
            <button data-testid={`sweep-rule-band-add-${scope}${rule.project}`} onClick={addBand} className="cf-touch inline-flex items-center gap-1 text-[11px] text-ink font-semibold">
              <Plus size={10} /> Band
            </button>
          )}
        </div>
        <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wider text-ink font-bold mb-2 pb-1 border-b border-hairline">
          <div className="col-span-5">Cumulative Threshold (₹Cr)</div>
          <div className="col-span-5">Sweep Rate (%)</div>
          <div className="col-span-2 text-right text-muted-light font-semibold">Delete</div>
        </div>
        <div className="space-y-1.5">
          {r.slab_bands.map((b, i) => {
            const lo = i === 0 ? 0 : (r.slab_bands[i - 1].upto_cr ?? 0);
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs">
                <div className="col-span-5 flex items-center gap-1">
                  <span className="text-muted-light">≥ ₹{lo}Cr →</span>
                  <input
                    type="number"
                    step="0.01"
                    value={b.upto_cr ?? ""}
                    disabled={!canEdit}
                    onChange={(e) => updBand(i, "upto_cr", e.target.value === "" ? null : parseFloat(e.target.value))}
                    placeholder="∞"
                    className="w-full px-2 py-1 bg-surface rounded text-xs num text-right"
                    data-testid={`band-upto-${scope}${rule.project}-${i}`}
                  />
                  <span className="text-muted-light">Cr</span>
                </div>
                <div className="col-span-5 flex items-center gap-1">
                  <input
                    type="number"
                    step="0.01"
                    value={(b.rate * 100).toFixed(2)}
                    disabled={!canEdit}
                    onChange={(e) => updBand(i, "rate", (parseFloat(e.target.value) || 0) / 100)}
                    className="w-full px-2 py-1 bg-surface rounded text-xs num text-right"
                    data-testid={`band-rate-${scope}${rule.project}-${i}`}
                  />
                  <span className="text-muted-light">%</span>
                </div>
                <div className="col-span-2 text-right">
                  {canEdit && (
                    <button onClick={() => delBand(i)} className="text-muted-dark hover:text-red-500" data-testid={`band-del-${scope}${rule.project}-${i}`}>
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Flat fee bands (optional) */}
      {(r.flat_fee_bands?.length > 0 || canEdit) && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">
              Flat monthly fee tiers (age-based, ₹L) — optional
            </div>
            {canEdit && (
              <button data-testid={`sweep-rule-fee-add-${scope}${rule.project}`} onClick={addFee} className="cf-touch inline-flex items-center gap-1 text-[11px] text-ink font-semibold">
                <Plus size={10} /> Tier
              </button>
            )}
          </div>
          {(r.flat_fee_bands || []).length > 0 && (
            <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wider text-ink font-bold mb-2 pb-1 border-b border-hairline">
              <div className="col-span-5">Upto (Months)</div>
              <div className="col-span-5">Fee (₹L)</div>
              <div className="col-span-2 text-right text-muted-light font-semibold">Delete</div>
            </div>
          )}
          {(r.flat_fee_bands || []).map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs mb-1.5">
              <div className="col-span-5 flex items-center gap-1">
                <span className="text-muted-light">Upto</span>
                <input
                  type="number"
                  value={b.upto_months ?? ""}
                  disabled={!canEdit}
                  onChange={(e) => updFee(i, "upto_months", e.target.value === "" ? null : parseInt(e.target.value))}
                  placeholder="∞"
                  className="w-full px-2 py-1 bg-surface rounded text-xs num text-right"
                />
                <span className="text-muted-light">mo</span>
              </div>
              <div className="col-span-5 flex items-center gap-1">
                <span className="text-muted-light">Fee</span>
                <input
                  type="number"
                  step="0.01"
                  value={b.fee_l}
                  disabled={!canEdit}
                  onChange={(e) => updFee(i, "fee_l", parseFloat(e.target.value) || 0)}
                  className="w-full px-2 py-1 bg-surface rounded text-xs num text-right"
                />
                <span className="text-muted-light">₹L</span>
              </div>
              <div className="col-span-2 text-right">
                {canEdit && (
                  <button onClick={() => delFee(i)} className="text-muted-dark hover:text-red-500">
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cutoff editor */}
      {canEdit && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <label>
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Cutoff date</div>
            <input
              type="text"
              value={r.cutoff_date}
              onChange={(e) => upd({ cutoff_date: e.target.value })}
              className="w-full px-2 py-1.5 bg-surface rounded text-xs num"
              data-testid={`sweep-rule-cutoff-date-${scope}${rule.project}`}
            />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Cutoff cumulative (₹Cr)</div>
            <input
              type="number"
              step="0.01"
              value={r.cutoff_cumulative_cr}
              onChange={(e) => upd({ cutoff_cumulative_cr: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 bg-surface rounded text-xs num text-right"
              data-testid={`sweep-rule-cutoff-cum-${scope}${rule.project}`}
            />
          </label>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Facility Editor ─────────────────────────── */

function FacilityEditor({ value, projects, onSave, onCancel }) {
  const [f, setF] = useState({
    ...value,
    project_names: [...(value.project_names || [])],
    slabs: [...(value.slabs || [])],
    project_shares: [...(value.project_shares || [])],
  });
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleProject = (name) => setF((x) => ({
    ...x,
    project_names: x.project_names.includes(name) ? x.project_names.filter((n) => n !== name) : [...x.project_names, name],
  }));
  const addSlab = () => upd("slabs", [...f.slabs, { threshold: 0, value: 0, value_type: "pct" }]);
  const setSlab = (i, k, v) => upd("slabs", f.slabs.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const delSlab = (i) => upd("slabs", f.slabs.filter((_, idx) => idx !== i));
  const setShare = (i, k, v) => upd("project_shares", f.project_shares.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const addShare = () => upd("project_shares", [...f.project_shares, { project: projects[0]?.name || "", share_pct: 100 }]);
  const delShare = (i) => upd("project_shares", f.project_shares.filter((_, idx) => idx !== i));

  return (
    <div className="cf-card p-5 cf-fade-up" data-testid="facility-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-lg text-ink font-bold">{f.id ? "Edit facility" : "New facility"}</div>
        <button onClick={onCancel} className="text-muted-dark"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <label><div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Facility name</div>
          <input data-testid="facility-name" value={f.name} onChange={(e) => upd("name", e.target.value)} className="w-full px-3 py-2 bg-surface rounded-lg" />
        </label>
        <label><div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Lender</div>
          <input data-testid="facility-lender" value={f.lender} onChange={(e) => upd("lender", e.target.value)} className="w-full px-3 py-2 bg-surface rounded-lg" />
        </label>
        <label><div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Type</div>
          <select data-testid="facility-kind" value={f.kind || "loan"} onChange={(e) => upd("kind", e.target.value)} className="w-full px-3 py-2 bg-surface rounded-lg">
            <option value="loan">Loan facility</option>
            <option value="fixed_obligation">Fixed obligation (EMI / fee)</option>
          </select>
        </label>
        <label><div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Sanctioned (₹L)</div>
          <input data-testid="facility-sanctioned" type="number" step="0.01" value={f.sanctioned} onChange={(e) => upd("sanctioned", parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 bg-surface rounded-lg num" />
        </label>
        <label><div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Interest rate (0-1)</div>
          <input data-testid="facility-rate" type="number" step="0.001" value={f.interest_rate} onChange={(e) => upd("interest_rate", parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 bg-surface rounded-lg num" />
        </label>
        <label><div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Opening balance (₹L)</div>
          <input data-testid="facility-opening" type="number" step="0.01" value={f.opening_balance} onChange={(e) => upd("opening_balance", parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 bg-surface rounded-lg num" />
        </label>
        <label><div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-1">Start date (YYYY-MM-01)</div>
          <input data-testid="facility-start" value={f.start_date} onChange={(e) => upd("start_date", e.target.value)} placeholder="2026-08-01" className="w-full px-3 py-2 bg-surface rounded-lg num" />
        </label>
      </div>

      {/* Sweep engine is preview-only — no per-facility toggle. */}
      <div className="mt-5 p-3 rounded-lg text-[11px] text-muted-dark" style={{ background: "var(--cf-surface-2)" }}>
        <span className="font-semibold text-ink">Principal source:</span> the saved Excel schedule / your manual cell edits.
        When a linked project has a sweep rule, engine-computed principal shows as a read-only preview row in the
        expanded schedule — manage rules in the <b>Sweep Rules</b> tab.
      </div>

      {/* Project shares (67/33 split enabler) */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Project shares (for split lenders)</div>
          <button data-testid="facility-share-add" onClick={addShare} className="cf-touch inline-flex items-center gap-1 text-xs text-ink font-medium">
            <Plus size={11} /> Share
          </button>
        </div>
        <div className="text-[11px] text-muted-dark mb-2">
          Leave empty to use <b>Linked projects</b> below at 100 % each. Set entries here for split-lender facilities (e.g. Imperia 67 % RRD / 33 % LO).
        </div>
        {f.project_shares.length > 0 && (
          <div className="space-y-2">
            {f.project_shares.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs">
                <div className="col-span-8">
                  <select
                    value={s.project}
                    onChange={(e) => setShare(i, "project", e.target.value)}
                    className="w-full px-2 py-1.5 bg-surface rounded text-xs"
                    data-testid={`share-project-${i}`}
                  >
                    {projects.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
                <div className="col-span-3 flex items-center gap-1">
                  <input
                    type="number"
                    step="0.01"
                    value={s.share_pct}
                    onChange={(e) => setShare(i, "share_pct", parseFloat(e.target.value) || 0)}
                    className="w-full px-2 py-1.5 bg-surface rounded text-xs num text-right"
                    data-testid={`share-pct-${i}`}
                  />
                  <span className="text-muted-light">%</span>
                </div>
                <div className="col-span-1 text-right">
                  <button onClick={() => delShare(i)} className="text-muted-dark hover:text-red-500"><Trash2 size={11} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-2">Sweep basis (legacy facility-side)</div>
        <div className="flex gap-2">
          {[{k:"collections", l:"Cumulative Collections"},{k:"age", l:"Loan Age (months)"}].map((o) => (
            <button
              key={o.k}
              data-testid={`facility-basis-${o.k}`}
              onClick={() => upd("sweep_basis", o.k)}
              className={`cf-touch px-3.5 py-2 rounded-full text-xs font-medium border ${f.sweep_basis === o.k ? "bg-ink text-white border-ink" : "bg-surface text-ink border-hairline"}`}
            >{o.l}</button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold mb-2">Linked projects</div>
        <div className="flex flex-wrap gap-2">
          {projects.map((p) => (
            <button
              key={p.id}
              data-testid={`facility-project-${p.name}`}
              onClick={() => toggleProject(p.name)}
              className={`cf-touch px-3 py-1.5 rounded-full text-[11px] font-medium border ${f.project_names.includes(p.name) ? "bg-ink text-white border-ink" : "bg-surface text-muted-dark border-hairline"}`}
            >{p.name}</button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Facility-side slab table (legacy)</div>
          <button data-testid="facility-slab-add" onClick={addSlab} className="cf-touch inline-flex items-center gap-1 text-xs text-ink font-medium"><Plus size={11} /> Band</button>
        </div>
        {f.slabs.length === 0 && <div className="text-xs text-muted-dark">Not needed when Sweep engine is on. Kept for edge-case facilities.</div>}
        <div className="space-y-2">
          {f.slabs.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-4">
                <input data-testid={`slab-threshold-${i}`} type="number" step="0.01" value={s.threshold}
                  onChange={(e) => setSlab(i, "threshold", parseFloat(e.target.value) || 0)}
                  placeholder={f.sweep_basis === "collections" ? "Threshold (₹Cr)" : "Threshold (months)"}
                  className="w-full px-2 py-1.5 bg-surface rounded text-xs num" />
              </div>
              <div className="col-span-3">
                <select data-testid={`slab-type-${i}`} value={s.value_type} onChange={(e) => setSlab(i, "value_type", e.target.value)} className="w-full px-2 py-1.5 bg-surface rounded text-xs">
                  <option value="pct">% rate</option>
                  <option value="fixed">Fixed ₹L</option>
                </select>
              </div>
              <div className="col-span-4">
                <input data-testid={`slab-value-${i}`} type="number" step="0.001" value={s.value}
                  onChange={(e) => setSlab(i, "value", parseFloat(e.target.value) || 0)}
                  placeholder={s.value_type === "pct" ? "e.g. 0.20 for 20%" : "Fixed ₹L"}
                  className="w-full px-2 py-1.5 bg-surface rounded text-xs num" />
              </div>
              <div className="col-span-1">
                <button data-testid={`slab-delete-${i}`} onClick={() => delSlab(i)} className="text-muted-dark hover:text-red-500"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">Monthly drawdowns (₹L)</div>
          <div className="text-[10px] text-muted-light">Enter planned drawdown per month · blank = 0</div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {nextMonths(f.start_date, 18).map((m) => (
            <label key={m} className="block">
              <div className="text-[10px] text-muted-light mb-0.5">{monthLabel(m)}</div>
              <input
                data-testid={`facility-drawdown-${m}`}
                type="number"
                step="0.01"
                value={f.drawdowns?.[m] ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = { ...(f.drawdowns || {}) };
                  if (v === "" || v === "0") delete next[m];
                  else next[m] = parseFloat(v) || 0;
                  upd("drawdowns", next);
                }}
                placeholder="0"
                className="w-full px-1.5 py-1 bg-surface rounded text-xs num text-right"
              />
            </label>
          ))}
        </div>
        {Object.values(f.drawdowns || {}).some((v) => v) && (
          <div className="mt-2 text-[11px] text-muted-dark">
            Total drawdown planned: <span className="num font-semibold text-ink">{formatCr(Object.values(f.drawdowns).reduce((a,b)=>a+(parseFloat(b)||0),0))}</span>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-2 text-xs text-muted-dark">Cancel</button>
        <button data-testid="facility-save" onClick={() => onSave(f)} className="cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-ink text-white text-sm font-medium">
          <Save size={13} /> Save facility
        </button>
      </div>
    </div>
  );
}
